import React, { useState } from 'react';
import {
  X,
  Share2,
  Copy,
  Check,
  Linkedin,
  Twitter,
  Mail,
  ExternalLink,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';

interface ViralShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab?: string;
  selectedAssetId?: string;
}

export const ViralShareModal: React.FC<ViralShareModalProps> = ({
  isOpen,
  onClose,
  activeTab = 'comps',
  selectedAssetId,
}) => {
  const [copied, setCopied] = useState(false);
  const [campaign, setCampaign] = useState<'ic_memo' | 'partner_diligence' | 'market_comps'>('partner_diligence');

  if (!isOpen) return null;

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://studio.aixenergy.io';
  const shareUrl = `${baseUrl}/?utm_source=deal_share&utm_medium=terminal&utm_campaign=${campaign}${
    selectedAssetId ? `&asset=${encodeURIComponent(selectedAssetId)}` : ''
  }`;

  const shareTitle = selectedAssetId
    ? `AIxEnergy Studio Deal Diligence · ${selectedAssetId}`
    : 'AIxEnergy Studio | AI Infrastructure Deal Terminal & Transaction Comps';

  const shareSummary =
    'Diligencing gigawatt AI data center transaction comps, queue-to-deed valuation arbitrage, and primary county deeds on AIxEnergy Studio.';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=600');
  };

  const handleShareTwitter = () => {
    const text = `Analyzing gigawatt AI data center transaction comps, powered land deeds, and queue arbitrage on @AIxEnergy Studio:`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(
      shareUrl
    )}&hashtags=DataCenters,AIInfrastructure,PrivateEquity,Energy`;
    window.open(url, '_blank', 'width=600,height=500');
  };

  const handleShareEmail = () => {
    const subject = `AIxEnergy Deal Studio Intelligence: ${selectedAssetId || 'Transaction Comps & Diligence'}`;
    const body = `Hi,\n\nI am reviewing transaction comps and valuation models on AIxEnergy Studio for gigawatt AI data center and energy infrastructure convergence.\n\nReview the terminal and deal comps here:\n${shareUrl}\n\nKey capabilities:\n- Primary County Deed and SEC EDGAR lease registry\n- Queue-to-Deed Valuation Arbitrage Screener\n- Distressed Queue Radar\n- Institutional 2-Page Investment Committee Memos\n\nAIxEnergy Studio (studio.aixenergy.io)`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="w-full max-w-lg bg-[#0c121e] border border-[#1a253b] rounded-2xl shadow-2xl text-slate-100 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 bg-[#090e1a] border-b border-[#1a253b] flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#2979ff]/15 text-[#2979ff] border border-[#2979ff]/30 font-bold uppercase">
              INSTITUTIONAL SHARING
            </span>
            <span className="text-xs text-slate-400 font-mono">VIRAL DEAL BRIEFING</span>
          </div>

          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white bg-[#121a2d] hover:bg-[#1a253b] border border-[#1a253b] transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          <div>
            <h3 className="text-base font-bold text-white">Share Deal Studio Intelligence</h3>
            <p className="text-xs text-slate-400 mt-1">
              Share real-time transaction comps, valuation spreads, and primary deeds with your Investment Committee, advisory partners, or deal team.
            </p>
          </div>

          {/* Social Card Preview */}
          <div className="p-3 rounded-xl bg-[#06090e] border border-[#1a253b] flex gap-3 items-center">
            <div className="w-16 h-16 rounded-lg bg-[#0c121e] border border-[#1a253b] flex items-center justify-center shrink-0 overflow-hidden">
              <img src="/aixenergy_logo.png" alt="AIxEnergy" className="w-12 h-12 object-contain" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-mono text-[#2979ff] uppercase tracking-wide">
                studio.aixenergy.io
              </div>
              <div className="text-xs font-bold text-white truncate mt-0.5">
                {shareTitle}
              </div>
              <div className="text-[11px] text-slate-400 line-clamp-1 mt-0.5">
                {shareSummary}
              </div>
            </div>
          </div>

          {/* Share Channels */}
          <div className="space-y-2">
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-400">
              One-Click Institutional Broadcast
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={handleShareLinkedIn}
                className="flex items-center justify-center space-x-1.5 py-2.5 px-3 rounded-lg bg-[#0A66C2]/15 hover:bg-[#0A66C2]/25 text-[#0A66C2] border border-[#0A66C2]/30 text-xs font-semibold transition"
              >
                <Linkedin className="w-4 h-4" />
                <span>LinkedIn</span>
              </button>

              <button
                onClick={handleShareTwitter}
                className="flex items-center justify-center space-x-1.5 py-2.5 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-semibold transition"
              >
                <Twitter className="w-4 h-4" />
                <span>X / Twitter</span>
              </button>

              <button
                onClick={handleShareEmail}
                className="flex items-center justify-center space-x-1.5 py-2.5 px-3 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/30 text-xs font-semibold transition"
              >
                <Mail className="w-4 h-4" />
                <span>Email Brief</span>
              </button>
            </div>
          </div>

          {/* Campaign Selector & Copy Link */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-400">
                Shareable Link with Attribution
              </label>
              <div className="flex items-center space-x-1 text-[10px] font-mono text-slate-500">
                <span>UTM:</span>
                <select
                  value={campaign}
                  onChange={(e) => setCampaign(e.target.value as any)}
                  className="bg-[#090e1a] border border-[#1a253b] rounded px-1.5 py-0.5 text-slate-300 text-[10px]"
                >
                  <option value="partner_diligence">Partner Diligence</option>
                  <option value="ic_memo">IC Memo</option>
                  <option value="market_comps">Market Comps</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 bg-[#06090e] border border-[#1a253b] rounded-lg px-3 py-2 text-xs font-mono text-slate-300 focus:outline-none focus:border-[#2979ff] select-all"
              />
              <button
                onClick={handleCopyLink}
                className={`flex items-center space-x-1 px-3.5 py-2 rounded-lg text-xs font-semibold transition shrink-0 ${
                  copied
                    ? 'bg-emerald-500 text-white'
                    : 'bg-[#2979ff] hover:bg-[#1c68e8] text-white shadow-sm'
                }`}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </div>

          <div className="pt-2 border-t border-[#1a253b] flex items-center justify-between text-[11px] text-slate-500 font-mono">
            <span className="flex items-center space-x-1">
              <ShieldCheck className="w-3.5 h-3.5 text-[#2979ff]" />
              <span>Attribution: AIxEnergy by Brandon N. Owens</span>
            </span>
            <span>studio.aixenergy.io</span>
          </div>
        </div>
      </div>
    </div>
  );
};
